import { useEffect, useState } from 'react';
import { Phone, MessageCircle, Globe, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Json } from '@/integrations/supabase/types';

interface Hotline {
  name: string;
  numbers: string[];
  hours?: string;
  website?: string;
}

interface EmergencyContact {
  id: string;
  country_name: string;
  country_code: string | null;
  emergency_number: string | null;
  hotlines: Json;
  resources: Json;
}

interface EmergencyContactCardProps {
  country: string;
}

export default function EmergencyContactCard({ country }: EmergencyContactCardProps) {
  const { t } = useTranslation('crisis');
  const [contact, setContact] = useState<EmergencyContact | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchContact() {
      setLoading(true);
      try {
        // Try exact match first, then fuzzy match
        let { data, error } = await supabase
          .from('emergency_contacts')
          .select('*')
          .eq('country_name', country)
          .maybeSingle();

        // If no exact match, try with country code or partial match
        if (!data && !error) {
          const result = await supabase
            .from('emergency_contacts')
            .select('*')
            .or(`country_name.ilike.%${country}%,country_code.ilike.${country}`)
            .limit(1)
            .maybeSingle();
          
          data = result.data;
          error = result.error;
        }

        if (error) {
          console.error('Error fetching emergency contact:', error);
        } else if (data) {
          setContact(data as EmergencyContact);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    }

    if (country) {
      fetchContact();
    }
  }, [country]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-earth-400" />
        </CardContent>
      </Card>
    );
  }

  if (!contact) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-earth-600">
            {t('noDataAvailable', 'Emergency contact information not available for this location.')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Emergency Number */}
      {contact.emergency_number && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-900">
              {t('emergencyNumber')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <a
              href={`tel:${contact.emergency_number.replace(/[^0-9+]/g, '')}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg"
            >
              <Phone className="h-6 w-6" />
              {contact.emergency_number}
            </a>
          </CardContent>
        </Card>
      )}

      {/* Crisis Hotlines */}
      {contact.hotlines && Array.isArray(contact.hotlines) && contact.hotlines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('crisisHotlines', '24/7 Crisis Hotlines')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {(contact.hotlines as unknown as Hotline[]).map((hotline, index) => (
              <div key={index} className="border-b border-earth-200 last:border-0 pb-6 last:pb-0">
                <h3 className="font-semibold text-lg text-earth-800 mb-3">
                  {hotline.name}
                </h3>
                {hotline.hours && (
                  <p className="text-sm text-earth-600 mb-3">
                    {t('hours', 'Hours')}: {hotline.hours}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {hotline.numbers.map((number, idx) => {
                    // Improved SMS detection: starts with "Text" (case insensitive)
                    const isTextLine = /^text\s/i.test(number.trim());
                    
                    if (isTextLine) {
                      // Extract the short code or number after "Text" keyword
                      const textMatch = number.match(/text\s+(?:(\w+)\s+to\s+)?(\d+)/i);
                      const shortCode = textMatch ? (textMatch[2] || textMatch[1]) : '';
                      const keyword = textMatch && textMatch[1] ? textMatch[1] : 'HOME';
                      
                      return (
                        <a
                          key={idx}
                          href={`sms:${shortCode}?&body=${encodeURIComponent(keyword)}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          <MessageCircle className="h-5 w-5" />
                          {number}
                        </a>
                      );
                    }
                    
                    // For phone numbers: preserve spaces in short codes (e.g., "13 11 14" -> "131114")
                    // but keep dashes and plus signs for international numbers
                    const cleanNumber = number.replace(/\s+/g, '').replace(/[^0-9+-]/g, '');
                    
                    return (
                      <a
                        key={idx}
                        href={`tel:${cleanNumber}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        <Phone className="h-5 w-5" />
                        {number}
                      </a>
                    );
                  })}
                  {hotline.website && (
                    <a
                      href={hotline.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                    >
                      <Globe className="h-5 w-5" />
                      {t('visitWebsite', 'Visit Website')}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
